import { Link } from "react-router-dom";
import { ArrowUpRight, Battery, Satellite, X } from "lucide-react";
import { KV, StatusBadge } from "@/components/common";
import { QuickActions } from "@/components/QuickActions";
import { EngineCommand } from "@/components/EngineCommand";
import { VehicleTypeIcon } from "@/components/VehicleRow";
import { STATUS, fmtDateTime, timeAgo, vehicleStatus, isGpsFresh } from "@/lib/fleet";

export function SpeedGauge({ speed = 0, limit = 80, status }) {
  const pct = Math.min(1, speed / 160);
  const r = 52;
  const len = Math.PI * r;
  const color = speed > limit ? "#EF4444" : STATUS[status]?.hex || "#EAB308";
  return (
    <div className="relative mx-auto h-[82px] w-[140px]" data-testid="speed-gauge">
      <svg viewBox="0 0 120 66" className="h-full w-full">
        <path d="M8 60 A52 52 0 0 1 112 60" fill="none" stroke="hsl(var(--border))" strokeWidth="8" strokeLinecap="round" />
        <path d="M8 60 A52 52 0 0 1 112 60" fill="none" stroke={color} strokeWidth="8" strokeLinecap="round" strokeDasharray={len} strokeDashoffset={len * (1 - pct)} style={{ transition: "stroke-dashoffset .8s ease, stroke .3s" }} />
      </svg>
      <div className="absolute inset-x-0 bottom-0 text-center">
        <div className="font-mono text-3xl font-bold leading-none" data-testid="speed-value">{Math.round(speed)}</div>
        <div className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">km/j · batas {limit}</div>
      </div>
    </div>
  );
}

export function VehicleInfoPanel({ v, onClose, onLocate, showCommand = true }) {
  const st = vehicleStatus(v);
  return (
    <div className="space-y-4 p-4" data-testid="vehicle-info-panel">
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-accent"><VehicleTypeIcon type={v.type} className="h-5 w-5" /></div>
          <div className="min-w-0">
            <div className="truncate text-base font-bold" data-testid="info-vehicle-name">{v.name}</div>
            <div className="font-mono text-xs text-muted-foreground" data-testid="info-vehicle-plate">{v.plate}</div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <StatusBadge status={st} testId="info-status" />
          {onClose && <button onClick={onClose} className="rounded-lg p-1 text-muted-foreground hover:bg-accent" data-testid="info-close-btn"><X className="h-4 w-4" /></button>}
        </div>
      </div>
      <SpeedGauge speed={isGpsFresh(v) ? (v.speed || 0) : 0} limit={v.speed_limit} status={st} />
      <div
        className={`mx-auto flex w-fit items-center gap-2 rounded-full border px-4 py-2 text-xs font-semibold ${
          v.power === true
            ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-500"
            : v.power === false
              ? "border-red-500/30 bg-red-500/10 text-red-500"
              : "border-slate-500/30 bg-slate-500/10 text-slate-400"
        }`}
        data-testid="info-power-indicator"
      >
        <span className={`h-2 w-2 rounded-full ${
          v.power === true ? "bg-emerald-500" : v.power === false ? "bg-red-500" : "bg-slate-400"
        }`} />
        POWER GPS · {v.power === true ? "ON" : v.power === false ? "OFF" : "BELUM ADA DATA"}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <KV label="IMEI" value={v.imei} testId="info-imei" />
        <KV label="ACC" value={<span className={v.acc ? "text-emerald-500" : "text-slate-400"}>{v.acc ? "ON" : "OFF"}</span>} testId="info-acc" />
        <KV label="Latitude" value={isGpsFresh(v) ? v.lat?.toFixed(6) : "—"} testId="info-lat" />
        <KV label="Longitude" value={isGpsFresh(v) ? v.lng?.toFixed(6) : "—"} testId="info-lng" />
        <KV label="Waktu GPS" value={fmtDateTime(v.gps_time)} testId="info-gps-time" />
        <KV label="Waktu Server" value={fmtDateTime(v.server_time)} testId="info-server-time" />
        <KV label="Koneksi" value={<span className={st === "offline" ? "text-slate-400" : "text-emerald-500"}>{st === "offline" ? "Terputus" : "Terhubung"} · {timeAgo(v.server_time)}</span>} mono={false} testId="info-connection" />
        <KV label="Arah" value={`${v.direction ?? 0}°`} />
      </div>
      <div className="flex items-center justify-between rounded-xl border border-border/70 px-3 py-2 text-xs text-muted-foreground">
        <span className="flex items-center gap-1.5"><Satellite className="h-3.5 w-3.5" /> {v.satellites ?? "-"} satelit</span>
        <span className="flex items-center gap-1.5"><Battery className="h-3.5 w-3.5" /> {"Power " + (v.power === true ? "ON" : v.power === false ? "OFF" : "BELUM ADA DATA")}</span>
        <span className="font-mono">{(v.odometer || 0).toLocaleString("id-ID", { maximumFractionDigits: 0 })} km</span>
      </div>
      <QuickActions vehicleId={v.id} onLocate={onLocate} />
      {showCommand && <EngineCommand vehicle={v} />}
      <Link to={`/vehicles/${v.id}`} className="flex items-center justify-center gap-1.5 rounded-xl bg-primary py-2.5 text-sm font-bold text-primary-foreground transition-opacity hover:opacity-90" data-testid="info-detail-link">
        Detail kendaraan <ArrowUpRight className="h-4 w-4" />
      </Link>
    </div>
  );
}
